import { Clock, Loader2, MapPin, Play } from 'lucide-react';
import { tradeLabel, useSalesTypes, type Trade } from '../../lib/trade.js';
import { useDialogA11y } from '../../lib/use-dialog-a11y.js';

interface ConfirmStartSheetProps {
  customerName: string;
  orderNo: string;
  /** Optional scheduled window label (e.g. "8a – 12p · 2h"). */
  scheduledWindowLabel: string | null;
  /** Optional site address one-liner. */
  addressLine: string | null;
  trade: Trade;
  /** True while the server-side start mutation is in flight — disables
   * the Start button + shows a spinner. */
  pending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * Pre-start confirmation modal. Surfaces enough job context for the tech
 * to verify they're starting the right stop, and explicitly states that
 * the timer + active-job lock fire immediately on confirm — preventing
 * accidental thumb-taps from starting the wrong visit.
 *
 * Park / resume of an already-active job bypasses this (DayScreen.onTapStop
 * routes those through `resumeActive` without going through this sheet).
 */
export function ConfirmStartSheet({
  customerName,
  orderNo,
  scheduledWindowLabel,
  addressLine,
  trade,
  pending,
  onCancel,
  onConfirm,
}: ConfirmStartSheetProps) {
  const salesTypes = useSalesTypes();
  const dialogRef = useDialogA11y<HTMLDivElement>(() => {
    if (!pending) onCancel();
  });
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Confirm start"
      className="fixed inset-0 z-40 bg-slate-950/40 flex items-end sm:items-center justify-center p-3"
      onClick={pending ? undefined : onCancel}
    >
      <div
        ref={dialogRef}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-xl shadow-xl p-4 space-y-3"
        style={{ backgroundColor: 'var(--color-elevated-surface)' }}
      >
        <div>
          <div className="text-[10.5px] uppercase tracking-wider text-ink-subtle">
            Ready to start
          </div>
          <div className="mt-1 text-base font-semibold text-ink">{customerName}</div>
          <div className="text-[11px] text-ink-muted">
            <span className="font-mono">{orderNo}</span> · {tradeLabel(salesTypes, trade)}
          </div>
        </div>

        {(scheduledWindowLabel || addressLine) && (
          <ul className="space-y-1 text-sm">
            {scheduledWindowLabel && (
              <li className="flex items-center gap-2 text-ink-muted">
                <Clock className="h-4 w-4 shrink-0" aria-hidden />
                <span>{scheduledWindowLabel}</span>
              </li>
            )}
            {addressLine && (
              <li className="flex items-center gap-2 text-ink-muted">
                <MapPin className="h-4 w-4 shrink-0" aria-hidden />
                <span className="truncate">{addressLine}</span>
              </li>
            )}
          </ul>
        )}

        <div
          className="rounded-md px-3 py-2 text-[12px] leading-snug"
          style={{
            backgroundColor: 'var(--feedback-info-bg)',
            color: 'var(--feedback-info-fg)',
          }}
        >
          Your timer begins now and locks you to this job until you clock
          out or park it. You can only run one job at a time.
        </div>

        <div className="flex gap-2 justify-end pt-1">
          <button
            type="button"
            onClick={onCancel}
            disabled={pending}
            className="rounded-md px-3 py-2 text-sm text-ink-muted hover:bg-surface-muted disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={pending}
            className="inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium text-white disabled:opacity-60"
            style={{ backgroundColor: 'var(--color-text-info)' }}
          >
            {pending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                Starting…
              </>
            ) : (
              <>
                <Play className="h-4 w-4" aria-hidden />
                Start job
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
